import {useCallback, useEffect, useState} from 'react';
import {Popover} from 'react-tiny-popover';
import urls from 'urls';
import {apiClient} from '~/api/api-client';
import {useRequestLogin} from '~/common/fabric/Card/components/LoginModal/hooks/useRequestLogin';
import {Button} from '~library/atoms/Button';
import {useTracking} from '~library/context/tracking/tracking-context';
import {useIsLargishAndUp} from '~library/hooks/useMatchQuery';
import {NotificationsView} from '~library/organisms/MegaNav/NotificationsView/NotificationsView';
import {NotificationLineIcon} from '~library/svg/NotificationLineIcon';
import {NotificationFillIcon} from '~library/svg/v2/NotificationFillIcon';
import * as styles from './Notifications.module.scss';

type Notification = {
  // biome-ignore-start lint/style/useNamingConvention: API properties
  created: string;
  read: boolean;
  text: string;
  re_user_image_url?: string;
  re_user_initials?: string;
  view_details_url?: string;
  // biome-ignore-end lint/style/useNamingConvention: API properties
};

type NotificationObject = {
  notifications: Notification[];
  unviewed: number;
  unread: number;
};

// All the logic from this component was copied from:
// ecl/organisms/MegaNav/MegaNav.js
// This component will be re-written in the following up release!
export const Notifications = () => {
  const isLargishAndUp = useIsLargishAndUp();
  const {loggedIn, requestLogin} = useRequestLogin();
  const {track} = useTracking();
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);
  const [notificationsObj, setNotificationsObj] = useState<NotificationObject>({
    notifications: [],
    unviewed: 0,
    unread: 0,
  });

  const onClickToggleNotificationDrawer = useCallback(() => {
    if (isLargishAndUp) {
      track({
        dataQaId: 'notifications-button',
        action: notificationsDrawerOpen ? 'close' : 'open',
      });
      setNotificationsDrawerOpen((prevValue) => !prevValue);
    } else {
      track({
        dataQaId: 'notifications-button-mobile',
        href: '/all-notifications',
      });
      window.location.href = '/all-notifications';
    }
  }, [isLargishAndUp, notificationsDrawerOpen, track]);

  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiClient.get(urls.get('ajax_notify_items'));

      const res = await response.json();
      const data = res || {};
      data.notifications = data.notifications || [];
      setNotificationsObj(data);
    } catch (error) {
      evite.error('Failed to fetch notifications:', error);
    }
  }, []);

  useEffect(() => {
    if (loggedIn) {
      fetchNotifications();
    }
  }, [loggedIn, fetchNotifications]);

  /** Handle an update to the status of a notification (e.g. read, hidden, etc.) */
  const updateNotificationStates = useCallback(
    async (
      idsToUpdate: string | string[],
      prop: string,
      val: boolean,
      successCallback: () => void,
      errorCallback?: () => void
    ) => {
      const url = urls.get('ajax_notify_update');
      const payload = {messages: idsToUpdate};
      // @ts-ignore
      payload[prop] = val;
      try {
        await apiClient.post(url, payload, {cache: 'no-cache'});

        if (typeof successCallback === 'function') {
          successCallback();
        }
      } catch (error) {
        evite.error(`Error while posting to ${url} errorThrown=${error}`);
        if (errorCallback) {
          errorCallback();
        }
      }
    },
    []
  );

  /** Mark all notifications as read */
  const onMarkAllAsRead = useCallback(() => {
    updateNotificationStates(
      notificationsObj.notifications.filter((n) => !n.read).map((n) => n.created),
      'read',
      true,
      () => {
        const updatedNotifications = {...notificationsObj};
        updatedNotifications.notifications = notificationsObj.notifications.map((n) => ({
          ...n,
          read: true,
        }));
        updatedNotifications.unread = 0;
        updatedNotifications.unviewed = 0;
        setNotificationsObj(updatedNotifications);
      }
    );
  }, [notificationsObj, updateNotificationStates]);

  /** Mark a single notification as read */
  const onMarkOneAsRead = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      updateNotificationStates([id], 'read', true, () => {
        const updatedNotifications = {...notificationsObj};
        const idx = updatedNotifications.notifications.findIndex((n) => n.created === id);
        if (idx >= 0) {
          updatedNotifications.notifications[idx].read = true;
          if (updatedNotifications.unread > 0) updatedNotifications.unread--;
          if (updatedNotifications.unviewed > 0) updatedNotifications.unviewed--;
        }
        setNotificationsObj(updatedNotifications);
      });
    },
    [notificationsObj, updateNotificationStates]
  );

  /** Mark a single notification as unread */
  const onMarkOneAsUnread = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      updateNotificationStates([id], 'read', false, () => {
        const updatedNotifications = {...notificationsObj};
        const idx = updatedNotifications.notifications.findIndex((n) => n.created === id);
        if (idx >= 0) {
          updatedNotifications.notifications[idx].read = false;
          if (updatedNotifications.unread > 0) updatedNotifications.unread++;
        }
        setNotificationsObj(updatedNotifications);
      });
    },
    [notificationsObj, updateNotificationStates]
  );

  /** Hide a notification from the notification viewer */
  const onHideNotification = useCallback(
    (e: React.MouseEvent, id: string) => {
      e.preventDefault();
      updateNotificationStates(id, 'delete', true, () => {
        const updatedNotifications = {...notificationsObj};
        updatedNotifications.notifications = updatedNotifications.notifications.filter(
          (n) => n.created !== id
        );
        setNotificationsObj(updatedNotifications);
      });
    },
    [notificationsObj, updateNotificationStates]
  );

  const onRequestLoginHandler = useCallback(async () => {
    track({
      dataQaId: 'notifications-button-login',
    });
    if (await requestLogin()) {
      onClickToggleNotificationDrawer();
    }
  }, [requestLogin, onClickToggleNotificationDrawer, track]);

  if (!isLargishAndUp && !loggedIn) {
    return null;
  }

  return (
    <div className={styles.notification}>
      <Popover
        isOpen={notificationsDrawerOpen}
        positions={['bottom', 'top', 'right', 'left']}
        padding={10}
        onClickOutside={onClickToggleNotificationDrawer}
        content={
          <div className={styles.notificationDropdown}>
            <NotificationsView
              {...notificationsObj}
              onMarkAllAsRead={onMarkAllAsRead}
              onMarkOneAsRead={onMarkOneAsRead}
              onMarkOneAsUnread={onMarkOneAsUnread}
              onHideNotification={onHideNotification}
            />
          </div>
        }
        containerStyle={{zIndex: '99999'}}
        boundaryElement={document.body}
        align="end"
      >
        <Button
          onClick={loggedIn ? onClickToggleNotificationDrawer : onRequestLoginHandler}
          data-qa-id="notifications-button"
          variant="unstyled-button"
          className={styles.notificationButton}
        >
          {notificationsObj.notifications &&
          notificationsObj.notifications.filter((n) => !n.read).length > 0 ? (
            <NotificationFillIcon ratio={0.625} />
          ) : (
            <NotificationLineIcon ratio={0.625} />
          )}
        </Button>
      </Popover>
    </div>
  );
};
